// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { SearchParams } from '@api/constants';
import { withRole } from '@common/authorization/Roles';
import PageFooter from '@components/layout/PageFooter';
import {
  EamcPath,
  type LegacyEamcPath,
  LegacySettingsPath,
  PortalPath,
  ROLE_TYPE_ADMIN,
  SettingsPath,
} from '@portal/constants';
import { omitSearchParam, preserveCurrentSearch, withSearchParam } from '@utils/navigationSearch';
import { Tooltip } from 'antd';
import { ChevronDown, ChevronsLeft, ChevronsRight, ChevronUp } from 'lucide-react';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, matchPath, Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import Settings from './Settings';
import { type ReportKey, useIsAvailable } from './SettingsRegistry';
import {
  type GlobalPage,
  isGlobalPage,
  SETTINGS_ITEMS,
  type VisibleGroup,
  visibleNav,
} from './SettingsTabs';
import './Settings.less';

type LegacyRedirectRow = {
  from: LegacySettingsPath | LegacyEamcPath;
  to: SettingsPath | EamcPath;
  report?: ReportKey;
};

export const LEGACY_REDIRECTS: readonly LegacyRedirectRow[] = [
  { from: LegacySettingsPath.APPLICATIONS, to: EamcPath.APPLICATIONS },
  { from: LegacySettingsPath.ENDPOINTS, to: EamcPath.ENDPOINTS },
  { from: LegacySettingsPath.ADVANCED_SETTINGS, to: EamcPath.WORKFLOWS },
  { from: LegacySettingsPath.EPCS_ACTIVITY_REPORT, to: EamcPath.REPORTS, report: 'epcs-activity' },
  { from: LegacySettingsPath.CUSTOMIZE, to: SettingsPath.BRANDING_AND_LABELS },
  {
    from: LegacySettingsPath.LOGIN_ACTIVITY_REPORT,
    to: SettingsPath.HUB_REPORTS,
    report: 'login-activity',
  },
  {
    from: LegacySettingsPath.ENROLLMENT_ACTIVITY_REPORT,
    to: SettingsPath.HUB_REPORTS,
    report: 'enrollment-activity',
  },
];

export const LegacyRedirect = ({ to, report }: LegacyRedirectRow) => {
  const [searchParams] = useSearchParams();
  const target = report
    ? withSearchParam(to, searchParams, SearchParams.REPORT, report)
    : preserveCurrentSearch(to, searchParams);
  return <Navigate to={target} replace />;
};

export const GlobalItemRoute = ({ item }: { item: GlobalPage }) => {
  const [searchParams] = useSearchParams();
  const available = useIsAvailable();

  if (!available(item.gate)) {
    return <Navigate to={preserveCurrentSearch(PortalPath.SETTINGS, searchParams)} replace />;
  }
  return <Settings item={item} />;
};

const PAGES = SETTINGS_ITEMS.filter(isGlobalPage);

type GroupKey = VisibleGroup['key'];

const holds = (group: VisibleGroup, page: GlobalPage | undefined) =>
  group.kind === 'page' ? group.page === page : group.entries.some(entry => entry.page === page);

const SettingsLayout = () => {
  const { t } = useTranslation();
  const { pathname, key: entryKey } = useLocation();
  const [searchParams] = useSearchParams();
  const available = useIsAvailable();
  const [overrides, setOverrides] = useState<
    Partial<Record<GroupKey, { open: boolean; at: string }>>
  >({});
  const [railed, setRailed] = useState(false);
  const focusOnExpand = useRef<GroupKey | null>(null);

  const activePage = PAGES.find(page => matchPath(page.routes.global, pathname) !== null);
  const productLabel = t('portal.settings.title');
  const tabTitle = activePage
    ? t('portal.settings.browser-title', { product: productLabel, page: t(activePage.titleKey) })
    : productLabel;

  useEffect(() => {
    const previous = document.title;
    document.title = tabTitle;
    return () => {
      document.title = previous;
    };
  }, [tabTitle]);

  const groups = visibleNav(available);
  const activeGroup = groups.find(group => holds(group, activePage))?.key;
  const navSearch = omitSearchParam(searchParams, SearchParams.REPORT);
  const setOpen = (key: GroupKey, open: boolean) =>
    setOverrides(previous => ({ ...previous, [key]: { open, at: entryKey } }));
  const isOpen = (key: GroupKey) => {
    const override = overrides[key];
    if (key === activeGroup && override?.at !== entryKey) {
      return true;
    }
    return override?.open ?? false;
  };
  const focusIfExpanded = (key: GroupKey) => (button: HTMLButtonElement | null) => {
    if (button && focusOnExpand.current === key) {
      focusOnExpand.current = null;
      button.focus();
    }
  };

  // Always wrapped, so railing doesn't remount the control and drop its focus.
  const railTip = (label: string, control: ReactElement) => (
    <Tooltip title={railed ? label : undefined} placement="right" trigger={['hover', 'focus']}>
      {control}
    </Tooltip>
  );

  const pageLink = (page: GlobalPage, label: string, icon?: ReactElement) =>
    railTip(
      label,
      <Link
        to={preserveCurrentSearch(page.routes.global, navSearch)}
        aria-current={page === activePage ? 'page' : undefined}
        aria-label={railed ? label : undefined}
        className={icon ? 'global-nav-row' : 'global-nav-item'}
      >
        {icon}
        {!railed && label}
      </Link>,
    );

  const row = (group: VisibleGroup) => {
    const { key, icon: Icon } = group;
    const label = t(group.labelKey);
    const icon = <Icon size={16} aria-hidden />;
    if (group.kind === 'page') {
      return pageLink(group.page, label, icon);
    }
    const className = key === activeGroup ? 'global-nav-row active' : 'global-nav-row';
    if (railed) {
      return railTip(
        label,
        <button
          type="button"
          className={className}
          aria-label={label}
          aria-current={key === activeGroup ? 'true' : undefined}
          onClick={() => {
            focusOnExpand.current = key;
            setRailed(false);
            setOpen(key, true);
          }}
        >
          {icon}
        </button>,
      );
    }
    const open = isOpen(key);
    const listId = `global-nav-${key}`;
    const Caret = open ? ChevronUp : ChevronDown;
    return (
      <>
        <button
          ref={focusIfExpanded(key)}
          type="button"
          className={className}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          onClick={() => setOpen(key, !open)}
        >
          {icon}
          <span className="global-nav-label">{label}</span>
          <Caret size={16} aria-hidden />
        </button>
        {open && (
          <ul id={listId}>
            {group.entries.map(entry => (
              <li key={entry.page.key}>{pageLink(entry.page, t(entry.labelKey))}</li>
            ))}
          </ul>
        )}
      </>
    );
  };

  const Toggle = railed ? ChevronsRight : ChevronsLeft;
  const toggleLabel = t(railed ? 'portal.settings.nav.expand' : 'portal.settings.nav.collapse');

  return (
    <div className="global-settings">
      <nav
        aria-label={t('portal.settings.nav.label')}
        className={railed ? 'global-nav global-nav-railed' : 'global-nav'}
      >
        <ul>
          {groups.map(group => (
            <li key={group.key}>{row(group)}</li>
          ))}
        </ul>
        {railTip(
          toggleLabel,
          <button
            type="button"
            className="global-nav-toggle"
            aria-label={toggleLabel}
            onClick={() => setRailed(!railed)}
          >
            <Toggle size={20} aria-hidden />
          </button>,
        )}
      </nav>
      <div className="global-settings-content">
        <Outlet />
        <PageFooter />
      </div>
    </div>
  );
};

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(SettingsLayout);
